/**
 * Slash-command catalog — fetched dynamically from the cowork backend.
 *
 * The list isn't hardcoded: ``GET /api/slash-commands`` asks the live
 * Claude Code CLI install (via the SDK's ``get_server_info``) what
 * ``/commands`` it actually understands — built-ins, plus the user's
 * installed skills, project skills, and plugin commands. That keeps the
 * chat palette in sync with the rest of the user's Claude Code setup
 * instead of going stale every time Anthropic ships a new command.
 *
 * Concurrency model: one in-flight fetch per page load is multiplexed
 * through a shared promise so re-renders of ``<ChatInput />`` don't
 * double-spawn the backend probe (each probe is a ~3 s subprocess on
 * the server). After a successful fetch the result is cached for the
 * page's lifetime; a manual ``reloadSlashCommands`` is exposed for
 * future "reload skills" affordances.
 */

import { apiFetch, ApiError } from "@/api/client";

export interface SlashCommand {
  /** Command name without the leading slash, e.g. ``clear`` or ``commit-commands:commit``. */
  name: string;
  /** Short, one-line description. Marker tags like ``(user)`` are already stripped server-side. */
  description: string;
  /** Argument hint to show faintly after the name, e.g. ``[name]`` or ``<instruction>``. May be empty. */
  args: string;
  /** Where the command comes from — drives the small badge in the menu. */
  scope: "builtin" | "user" | "project" | "plugin";
  /** Plugin id when ``scope === "plugin"``, otherwise ``null``. */
  plugin: string | null;
}

interface SlashCommandsResponse {
  commands: SlashCommand[];
}

let cache: SlashCommand[] | null = null;
let inflight: Promise<SlashCommand[]> | null = null;

/**
 * Returns the catalog, fetching on first call and reusing the cached
 * result thereafter. A second call while a fetch is already in flight
 * joins the same promise rather than triggering another request.
 */
export async function getSlashCommands(force = false): Promise<SlashCommand[]> {
  if (!force && cache !== null) return cache;
  if (!force && inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await apiFetch<SlashCommandsResponse>(
        force ? "/slash-commands?refresh=true" : "/slash-commands",
      );
      cache = Array.isArray(data.commands) ? data.commands : [];
      return cache;
    } catch (err) {
      // 503 — backend couldn't reach the CLI. Cache an empty list so the
      // menu still opens (with a "no commands found" empty state) and the
      // user can keep typing without the request hammering on every key.
      if (err instanceof ApiError && err.status === 503) {
        cache = [];
        return cache;
      }
      throw err;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Force the next call to re-fetch from the backend. */
export function reloadSlashCommands(): Promise<SlashCommand[]> {
  cache = null;
  return getSlashCommands(true);
}

/** Synchronously read the currently-cached catalog (empty until first fetch). */
export function getCachedSlashCommands(): SlashCommand[] {
  return cache ?? [];
}

/**
 * Filter commands by a user-typed query (after the leading ``/``).
 *
 * Prefix matches rank ahead of substring matches. An empty query returns
 * the full list in declaration order — the backend already groups by
 * scope/plugin in the order returned by the SDK, which is the same order
 * Claude Code's own ``/`` palette uses.
 */
export function filterSlashCommands(
  query: string,
  commands: SlashCommand[],
): SlashCommand[] {
  const q = query.replace(/^\//, "").toLowerCase().trim();
  if (!q) return commands;
  const prefix: SlashCommand[] = [];
  const substring: SlashCommand[] = [];
  for (const cmd of commands) {
    const name = cmd.name.toLowerCase();
    if (name.startsWith(q)) prefix.push(cmd);
    else if (name.includes(q)) substring.push(cmd);
  }
  return [...prefix, ...substring];
}

/**
 * Token returned by :func:`tokenizeSlashCommands` — used by the highlight
 * overlay to render recognised slash commands in jove purple.
 */
export interface SlashToken {
  type: "text" | "command";
  value: string;
}

/** Escape regex metacharacters in command names (``:`` and ``-`` survive). */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a regex that matches any of the known command names as a slash
 * token. Sorted longest-first so e.g. ``/security-review`` wins over
 * ``/review`` and ``/commit-commands:commit-push-pr`` over the shorter
 * ``/commit-commands:commit``.
 */
function buildRegex(commands: SlashCommand[]): RegExp | null {
  if (commands.length === 0) return null;
  const names = commands
    .map((c) => c.name)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegex);
  return new RegExp(
    `(^|\\s)(\\/(?:${names.join("|")}))(?=$|[\\s.,;:!?])`,
    "g",
  );
}

/**
 * Split ``text`` into a stream of plain-text and slash-command tokens.
 * Concatenating ``token.value`` gives back the original ``text`` so the
 * overlay div maintains the exact same layout as the underlying textarea.
 */
export function tokenizeSlashCommands(
  text: string,
  commands: SlashCommand[],
): SlashToken[] {
  if (!text) return [];
  const regex = buildRegex(commands);
  if (!regex) return [{ type: "text", value: text }];

  const tokens: SlashToken[] = [];
  let lastIndex = 0;
  regex.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const leading = match[1] ?? "";
    const command = match[2] ?? "";
    const cmdStart = match.index + leading.length;
    if (cmdStart > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, cmdStart) });
    }
    tokens.push({ type: "command", value: command });
    lastIndex = cmdStart + command.length;
  }
  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }
  return tokens;
}
